import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import type { FiuuTopUpPollOutcome, FiuuTopUpState, TopUpReturnHint } from '@mypoz/shared';
import { isAccDetailId, pollFiuuTopUp, toTopUpReturnHint } from '@mypoz/shared';
import { Card, Icon, cn } from '@mypoz/ui';

import { useAccountSession } from '@/features/account/context.js';
import { fetchFiuuTopUpState } from '@/features/wallet/api.js';
import type { TopUpFormLocationState } from '@/features/wallet/components/TopUpForm.js';
import { clearLastTopUp, isPlainOrderId, readLastTopUp } from '@/features/wallet/lastTopUpOrder.js';
import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';
import { WALLET_BALANCE_ROUTE } from '@/shell/navigation.js';

/**
 * The top-up result page (MYP-134) at
 * `/wallet/top-up/result?orderid=…&accdetailid=…&status=success|cancelled|failed&sign=…[&cancelled=1]`,
 * where the vendor sends the customer back after Fiuu (its guide of 2026-10-26, section 4.1).
 *
 * It never takes the result from the address: it finds the top-up in `get_trade` until the record
 * is paid (END) or handled by staff. `status=cancelled` (or `cancelled=1`) and `status=failed` only
 * ever turn a still-unpaid record into "you cancelled" or "payment failed" — a message, never
 * money (`toTopUpOutcome`). `status=success` and `sign` are not relied on: only `get_trade` says a
 * top-up is paid.
 *
 * Not tied to `FIUU_TOPUP_ENABLED`: the vendor always sends the customer to web.mypoz.com, so this
 * page has to work on the live site before top-up is switched on there — for the first real test,
 * started from a developer's machine. It only reads; it starts nothing.
 */
export function TopUpResult() {
  const [searchParams] = useSearchParams();
  // Read once: the polling clears it as soon as it starts, and a later render must not lose it.
  const [saved] = useState(readLastTopUp);
  const fromAddress = searchParams.get('accdetailid');
  const orderidParam = searchParams.get('orderid');
  const orderid = isPlainOrderId(orderidParam) ? orderidParam : undefined;
  // The address first; the card's saved record id only when the address carries neither.
  const accdetailid = isAccDetailId(fromAddress)
    ? fromAddress
    : orderid === undefined
      ? saved
      : undefined;
  const hint = toTopUpReturnHint(searchParams.get('status'), searchParams.get('cancelled'));

  if (accdetailid === undefined && orderid === undefined) {
    return <ResultCard view={{ kind: 'notFound' }} />;
  }
  return (
    <PolledResult
      key={`${accdetailid ?? ''}|${orderid ?? ''}`}
      accdetailid={accdetailid}
      orderid={orderid}
      hint={hint}
    />
  );
}

type View =
  { readonly kind: 'checking' } | Exclude<FiuuTopUpPollOutcome, { readonly kind: 'stopped' }>;

function PolledResult({
  accdetailid,
  orderid,
  hint,
}: {
  accdetailid: string | undefined;
  orderid: string | undefined;
  hint: TopUpReturnHint | undefined;
}) {
  const { refresh } = useAccountSession();
  const [view, setView] = useState<View>({ kind: 'checking' });
  const [latest, setLatest] = useState<FiuuTopUpState | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // This page has taken over the top-up: the Top up card must not treat it as abandoned if the
    // customer goes back to it from here.
    clearLastTopUp();
    const controller = new AbortController();
    void pollFiuuTopUp({
      check: () => fetchFiuuTopUpState({ accdetailid, orderid }),
      hint,
      onState: setLatest,
      signal: controller.signal,
    }).then((outcome) => {
      if (outcome.kind === 'stopped') return;
      setView(outcome);
      // The balance is only ever the vendor's figure: read it again, without blanking the header.
      if (outcome.kind === 'final' && outcome.outcome === 'paid') void refresh();
    });
    return () => controller.abort();
  }, [accdetailid, orderid, hint, attempt, refresh]);

  const checkAgain = () => {
    setView({ kind: 'checking' });
    setAttempt((previous) => previous + 1);
  };

  const reference = latest?.reference ?? orderid ?? accdetailid;
  return <ResultCard view={view} reference={reference} latest={latest} onCheckAgain={checkAgain} />;
}

type Tone = 'brand' | 'success' | 'danger' | 'warning' | 'muted';

const TONE_CLASS: Record<Tone, string> = {
  brand: 'bg-brand-soft text-brand',
  success: 'bg-success/10 text-success',
  danger: 'bg-danger/10 text-danger',
  warning: 'bg-warning/10 text-warning',
  muted: 'bg-surface-alt text-muted',
};

/** One word per thing the page says differently. */
type Shown = 'checking' | 'paid' | 'handled' | 'failed' | 'cancelled' | 'timedOut' | 'notFound';

const LOOK: Record<Shown, { readonly icon: string; readonly tone: Tone }> = {
  checking: { icon: 'progress_activity', tone: 'brand' },
  paid: { icon: 'check_circle', tone: 'success' },
  handled: { icon: 'support_agent', tone: 'muted' },
  failed: { icon: 'cancel', tone: 'danger' },
  cancelled: { icon: 'undo', tone: 'muted' },
  timedOut: { icon: 'hourglass_top', tone: 'warning' },
  notFound: { icon: 'search_off', tone: 'muted' },
};

function toShown(view: View): Shown {
  return view.kind === 'final' ? view.outcome : view.kind;
}

function ResultCard({
  view,
  reference,
  latest,
  onCheckAgain,
}: {
  view: View;
  reference?: string;
  latest?: FiuuTopUpState;
  onCheckAgain?: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const shown = toShown(view);
  const look = LOOK[shown];
  const state = view.kind === 'final' ? view.state : latest;
  const amount = state !== undefined ? fmt.money(state.amountCents) : undefined;
  const paidAt = view.kind === 'final' ? view.state.paidAtIso : undefined;
  const retryState: TopUpFormLocationState | undefined =
    state !== undefined ? { topUpAmountCents: state.amountCents } : undefined;

  const tryAgain = (
    <Link to={WALLET_BALANCE_ROUTE} state={retryState} className={buttonClass('primary')}>
      {t('wallet.topUpResult.tryAgain')}
    </Link>
  );
  const backToWallet = (variant: 'primary' | 'secondary') => (
    <Link to={WALLET_BALANCE_ROUTE} className={buttonClass(variant)}>
      {t('wallet.topUpResult.backToWallet')}
    </Link>
  );

  return (
    <div className="mx-auto w-full max-w-lg">
      <Card className="flex flex-col items-center gap-4 text-center">
        <span
          aria-hidden="true"
          className={cn(
            'flex size-14 items-center justify-center rounded-full',
            TONE_CLASS[look.tone],
          )}
        >
          <Icon
            name={look.icon}
            size={30}
            className={shown === 'checking' ? 'animate-spin' : undefined}
          />
        </span>

        <div role={shown === 'checking' ? 'status' : 'alert'} className="flex flex-col gap-1.5">
          <h1 className="text-xl font-semibold text-ink">
            {t(`wallet.topUpResult.${shown}.title`, { amount: amount ?? '' })}
          </h1>
          <p className="text-sm text-muted">{t(`wallet.topUpResult.${shown}.body`)}</p>
        </div>

        {reference !== undefined ? (
          <dl className="grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-lg bg-surface-alt p-4 text-left text-sm">
            {amount !== undefined ? (
              <>
                <dt className="text-muted">{t('wallet.topUpResult.amount')}</dt>
                <dd className="text-right font-semibold text-ink tabular-nums">{amount}</dd>
              </>
            ) : null}
            <dt className="text-muted">{t('wallet.topUpResult.reference')}</dt>
            <dd className="text-right break-all text-ink">{reference}</dd>
            {paidAt !== undefined ? (
              <>
                <dt className="text-muted">{t('wallet.topUpResult.paidAt')}</dt>
                <dd className="text-right text-ink">{fmt.dateTime(paidAt)}</dd>
              </>
            ) : null}
          </dl>
        ) : null}

        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          {shown === 'paid' || shown === 'handled' || shown === 'notFound'
            ? backToWallet('primary')
            : null}
          {shown === 'failed' || shown === 'cancelled' ? (
            <>
              {backToWallet('secondary')}
              {tryAgain}
            </>
          ) : null}
          {shown === 'timedOut' ? (
            <>
              {backToWallet('secondary')}
              <button type="button" onClick={onCheckAgain} className={buttonClass('primary')}>
                {t('wallet.topUpResult.checkAgain')}
              </button>
            </>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
